<template>
  <Swiper :parentClass="'featured-slides'" :swiperOptions="swiperOptions">
    <div
      v-for="course in courses"
      :key="course.objectID"
      class="swiper-slide featured-slides__slide"
      :style="`background-image: url(${$getCourseBackground(
        course.objectID,
        getBackgroundSize()
      )}), linear-gradient(${$getGradient()});`"
    >
      <div class="featured-slides__overlay"></div>
      <div class="featured-slides__course-wrapper">
        <div class="featured-slides__branding">
          <p class="featured-slides__vendor">{{ course.vendor }}</p>
          <p
            v-if="course.has_sample_content && isFreeTrialUser"
            class="featured-slides__snipe"
          >
            Included
          </p>
          <p
            v-if="course.status === 'coming soon'"
            class="featured-slides__snipe"
          >
            Coming Soon
          </p>
        </div>
        <div class="featured-slides__title">{{ course.name }}</div>
        <div class="featured-slides__description">
          {{ shorten(course.description) }}
          <router-link
            v-if="course.description && course.description.length > 220"
            :to="$buildContentLink('course', course)"
            class="featured-slides__description--link"
          >
            See More
          </router-link>
        </div>
        <div class="featured-slides__buttons">
          <router-link
            class="btn btn--primary"
            :to="$buildContentLink('course', course)"
          >
            View Course
          </router-link>
        </div>
      </div>
    </div>
  </Swiper>
</template>

<script>
import { defineComponent, ref, onMounted, toRefs } from 'vue'
import Swiper from '@/components/layout/Swiper.vue'
import { createAlgoliaIndices } from '@/utils'
import { getBackgroundSizeBaseOnBreakpoint } from '@/helpers'

export default defineComponent({
  name: 'FeaturedCoursesSlider',
  components: {
    Swiper,
  },
  inject: ['getBreakpoint'],
  props: {
    algoliaKeys: {
      type: Object,
      required: true,
    },
    isFreeTrialUser: {
      type: Boolean,
      required: true,
    },
  },
  setup(props) {
    const { algoliaKeys } = toRefs(props)
    const swiperOptions = {
      loop: true,
      effect: 'slide',
      speed: 2500,
      autoplay: {
        delay: 7000,
      },
      freemode: false,
    }
    const courses = ref([])
    const shorten = value => {
      if (value.length > 220) {
        return `${value.substr(0, 220)}...`
      }
      return value
    }
    const handleResults = (_, { hits }) => {
      courses.value = hits
    }
    const createAlgoliaSearchInstance = () => {
      const {
        value: { appKey, appId, coursesIndex: index },
      } = algoliaKeys
      const options = {
        keys: {
          appId,
          appKey,
        },
        contexts: [
          {
            name: 'Courses',
            index,
            facets: ['is_featured'],
          },
        ],
        resultsHandler: handleResults,
      }
      const parentFilter = {
        facet: 'is_featured',
        value: 'true',
      }
      createAlgoliaIndices(options, parentFilter)
    }
    onMounted(createAlgoliaSearchInstance)
    return {
      swiperOptions,
      courses,
      shorten,
      handleResults,
      createAlgoliaSearchInstance,
    }
  },
  methods: {
    getBackgroundSize() {
      const breakpoint = this.getBreakpoint()
      return getBackgroundSizeBaseOnBreakpoint(breakpoint)
    },
  },
})
</script>

<style lang="scss">
.featured-slides {
  @include mobile {
    min-height: 605px;
  }
  &__slide {
    color: var(--white);
    background-size: cover;
    background-repeat: no-repeat;
    background-position: left;
    z-index: 1;
    position: relative;
    height: 100%;
    padding: 40px 0 0 0;
    @include mobile {
      min-height: 575px;
      background-position: inherit;
    }
  }
  &__overlay {
    position: absolute;
    right: 0;
    top: 0;
    left: 0;
    bottom: 0;
    background: linear-gradient(
      to bottom,
      rgba(0, 0, 0, 0),
      rgba(0, 0, 0, 0),
      rgba(0, 0, 0, 0.4),
      rgba(0, 0, 0, 0.6),
      var(--dark-gray)
    );
    z-index: -1;
  }
  &__course-wrapper {
    display: flex;
    flex-direction: column;
    padding: 70px 20px 40px 20px;
    width: 100%;
    @include desktop {
      padding: 0 150px;
      margin: 100px 0 0 0;
    }
  }
  &__branding {
    margin: 0 0 15px 0;
    width: 100%;
    display: flex;
    align-items: center;
  }
  &__vendor {
    font-size: 14px;
    font-family: var(--font-medium);
    text-transform: uppercase;
    margin: 0 15px 0 0;
  }
  &__snipe {
    margin: 0;
    font-family: var(--font-bold);
    text-transform: uppercase;
    font-size: 0.8rem;
    background: var(--black);
    padding: 5px 15px;
  }
  &__title {
    font-size: 28px;
    font-family: var(--font-bold);
    letter-spacing: -1.04px;
    line-height: 2.2rem;
    width: 90%;
    margin: 0 0 25px 0;
    @include mobile {
      line-height: 3rem;
    }
    @include tablet {
      font-size: 36px;
    }
    @include desktop {
      width: 50%;
    }
  }
  &__description {
    font-family: var(--font-medium);
    letter-spacing: -0.36px;
    font-size: 18px;
    width: 90%;
    margin: 0 0 auto 0;
    overflow-wrap: break-word;
    word-wrap: break-word;
    text-overflow: ellipsis;
    overflow-y: auto;
    @include desktop {
      width: 45%;
      margin: 0 0 25px 0;
    }
    &--link {
      font-size: rem(12px);
      color: var(--orange);
      text-transform: uppercase;
      &:visited {
        color: var(--orange);
      }
      &:hover {
        opacity: 0.8;
      }
    }
  }
  &__buttons {
    display: flex;
    flex-wrap: nowrap;
    width: 100%;
    align-items: center;
    margin: 50px 0 0 0;
  }
  .swiper-button-prev,
  .swiper-button-next {
    background: transparent;
  }
}
</style>
